// 캐릭터 이름 규칙(docs/CHARACTER-PRD.md §4 `이름#태그`): 클라이언트(실시간 형식 검사)와 Worker(최종 판정)가 같은 함수를 쓴다.
// - 이름 2~8자, 한글 완성형·영문·숫자만(공백·기호·이모지·자모·전각·다른 문자 체계 불가). NFC 정규화.
// - 예약어(커뮤니티 기존 목록 tll-social filter + 게임 목록) · 금칙어(욕설·광고) · 동형 문자 접기(0/O, 1/l/I, rn/m …)로 사칭 이름 거부.
// - 정규화 이름(norm): 소문자 + 동형 문자 접기. 태그는 같은 norm 안에서 겹치지 않는다 → "Kael0#1234"와 "KaelO#1234"가 함께 있을 수 없다.
import { DEFAULT_AD_WORDS, DEFAULT_BANNED_WORDS, DEFAULT_RESERVED_EN_AFFIX, DEFAULT_RESERVED_EN_EXACT, DEFAULT_RESERVED_KO, isReservedName } from '../../packages/tll-social/src/shared/filter';
import { SOCIAL_CONFIG } from '../community/config';
import type { NameReason } from './protocol';

export const NAME_MIN = 2;
export const NAME_MAX = 8;
/** 한글 완성형(가~힣)·영문·숫자 */
const NAME_CHARSET = /^[가-힣A-Za-z0-9]+$/;
/** 게임이 더하는 예약어(커뮤니티 reservedNames + 캐릭터 전용). 부분 문자열, 접은 형태로 비교 */
const EXTRA_RESERVED: readonly string[] = [...(SOCIAL_CONFIG.reservedNames ?? []), '게임마스터', '고객센터', '공지사항'];

export type NameCheck = { ok: true; name: string; norm: string } | { ok: false; reason: NameReason };

/** 동형 문자 접기(소문자 입력). 숫자는 닮은 영문자로, l·I는 i로, rn→m, vv→w */
const DIGIT_FOLD: Readonly<Record<string, string>> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '6': 'g', '7': 't', '8': 'b', '9': 'g' };
export function foldHomoglyphs(lower: string): string {
  let s = '';
  for (const ch of lower) s += DIGIT_FOLD[ch] ?? (ch === 'l' ? 'i' : ch);
  return s.replace(/rn/g, 'm').replace(/vv/g, 'w');
}

const fold = (w: string): string => foldHomoglyphs(w.normalize('NFC').toLowerCase());
const KO_FOLDED = [...DEFAULT_RESERVED_KO, ...EXTRA_RESERVED].map(fold);
const EXACT_FOLDED = new Set(DEFAULT_RESERVED_EN_EXACT.map(fold));
const AFFIX_FOLDED = DEFAULT_RESERVED_EN_AFFIX.map(fold);
/** 접은 이름으로 보는 예약어 검사(tll-social isReservedName과 같은 규칙을 접은 목록에 적용 — 'adm1n'·'0fficial'·'5ystem') */
function reservedFolded(norm: string): boolean {
  if (KO_FOLDED.some((w) => w && norm.includes(w))) return true;
  for (const token of norm.match(/[a-z]+/g) ?? []) {
    if (EXACT_FOLDED.has(token)) return true;
    if (AFFIX_FOLDED.some((w) => token.startsWith(w) || token.endsWith(w))) return true;
  }
  return false;
}

/** 정규화 이름(태그 유일 범위·예약어 비교 기준). 형식 검사를 통과한 이름에만 쓴다 */
export function normName(name: string): string {
  return foldHomoglyphs(name.normalize('NFC').toLowerCase());
}

/** 이름 형식·예약어·금칙어 검사. 앞뒤 공백은 잘라서 본다(가운데 공백은 거부) */
export function checkName(raw: unknown): NameCheck {
  if (typeof raw !== 'string') return { ok: false, reason: 'empty' };
  const name = raw.normalize('NFC').trim();
  if (!name) return { ok: false, reason: 'empty' };
  const len = [...name].length;
  if (!NAME_CHARSET.test(name)) return { ok: false, reason: 'charset' };
  if (len < NAME_MIN || len > NAME_MAX) return { ok: false, reason: 'length' };
  const lower = name.toLowerCase();
  const norm = foldHomoglyphs(lower);
  // 예약어: 원래 소문자와 접은 형태 둘 다(접기만 하면 'admin'이 'admin'으로 남지만 'adm1n'·'0fficial'은 접어야 걸린다)
  if (isReservedName(lower, [...EXTRA_RESERVED]) || reservedFolded(norm)) return { ok: false, reason: 'reserved' };
  const banned = [...DEFAULT_BANNED_WORDS, ...DEFAULT_AD_WORDS, ...(SOCIAL_CONFIG.bannedWords ?? [])];
  for (const w of banned) {
    const wl = w.normalize('NFC').toLowerCase();
    if (lower.includes(wl) || norm.includes(foldHomoglyphs(wl))) return { ok: false, reason: 'banned' };
  }
  return { ok: true, name, norm };
}

/** 이름 검사 결과 문구(UI) */
export function nameReasonText(reason: NameReason): string {
  switch (reason) {
    case 'empty': return '이름을 입력해 주세요';
    case 'length': return `이름은 ${NAME_MIN}~${NAME_MAX}자로 지어 주세요`;
    case 'charset': return '한글·영문·숫자만 쓸 수 있어요(공백·기호 불가)';
    case 'reserved': return '운영·공식으로 오해될 수 있는 이름이에요';
    case 'banned': return '쓸 수 없는 단어가 들어 있어요';
  }
}

/** `이름#1234` → {name, tag}(형식이 틀리면 null) */
export function parseHandle(raw: string): { name: string; tag: number } | null {
  const m = /^(.+)#(\d{4})$/.exec(raw.normalize('NFC').trim());
  if (!m) return null;
  const c = checkName(m[1]);
  const tag = Number(m[2]);
  return c.ok && tag >= 1 ? { name: c.name, tag } : null;
}
